import type { GoalArchetypeId, UserGoals } from '../types';

export interface GoalArchetype {
  id: GoalArchetypeId;
  title: string;
  tagline: string;
  targetWpm: number;
  targetAccuracy: number;
  targetCurriculumProgress: number;
}

export const GOAL_ARCHETYPES: GoalArchetype[] = [
  {
    id: 'rolig-skriver',
    title: 'Rolig skriver',
    tagline: 'Skriv roligt og sikkert — færre fejl end høj fart.',
    targetWpm: 28,
    targetAccuracy: 0.96,
    targetCurriculumProgress: 0.35,
  },
  {
    id: 'hverdagstastatur',
    title: 'Hverdagstastatur',
    tagline: 'Mail, beskeder og korte noter i hverdagen.',
    targetWpm: 42,
    targetAccuracy: 0.93,
    targetCurriculumProgress: 0.55,
  },
  {
    id: 'arbejdsrytme',
    title: 'Arbejdsrytme',
    tagline: 'Længere tekster uden at kigge på tastaturet.',
    targetWpm: 55,
    targetAccuracy: 0.91,
    targetCurriculumProgress: 0.7,
  },
  {
    id: 'fokusflow',
    title: 'Fokusflow',
    tagline: 'Produktiv skrivning til arbejde og studie.',
    targetWpm: 68,
    targetAccuracy: 0.92,
    targetCurriculumProgress: 0.85,
  },
  {
    id: 'touch-virtuos',
    title: 'Touch-virtuos',
    tagline: 'Høj hastighed med stærk præcision.',
    targetWpm: 80,
    targetAccuracy: 0.94,
    targetCurriculumProgress: 1,
  },
];

const ARCHETYPE_MAP = new Map(GOAL_ARCHETYPES.map((a) => [a.id, a]));

export function getGoalArchetype(id: GoalArchetypeId): GoalArchetype {
  return ARCHETYPE_MAP.get(id) ?? GOAL_ARCHETYPES[1];
}

export function goalsFromArchetype(id: GoalArchetypeId): UserGoals {
  const a = getGoalArchetype(id);
  return {
    archetypeId: a.id,
    targetWpm: a.targetWpm,
    targetAccuracy: a.targetAccuracy,
    targetCurriculumProgress: a.targetCurriculumProgress,
  };
}

export function isGoalArchetypeId(id: string): id is GoalArchetypeId {
  return ARCHETYPE_MAP.has(id as GoalArchetypeId);
}

/** Human-readable WPM range for picker UI (not shown as a hard exam target). */
export function archetypeWpmHint(id: GoalArchetypeId): string {
  const idx = GOAL_ARCHETYPES.findIndex((a) => a.id === id);
  if (idx < 0) return '';
  const current = GOAL_ARCHETYPES[idx]!;
  if (idx === 0) {
    const next = GOAL_ARCHETYPES[1]!.targetWpm;
    const low = Math.max(15, current.targetWpm - 5);
    const high = Math.round((current.targetWpm + next) / 2) - 1;
    return `ca. ${low}–${high} wpm`;
  }
  if (idx === GOAL_ARCHETYPES.length - 1) {
    return `ca. ${current.targetWpm}+ wpm`;
  }
  const prev = GOAL_ARCHETYPES[idx - 1]!.targetWpm;
  const next = GOAL_ARCHETYPES[idx + 1]!.targetWpm;
  const low = Math.round((prev + current.targetWpm) / 2);
  const high = Math.round((current.targetWpm + next) / 2);
  return `ca. ${low}–${high} wpm`;
}
